import React, { useRef, useState } from 'react';
import { Upload, Download, CheckCircle, Trash2, Loader2 } from 'lucide-react';
import { downloadIncentiveTemplateAPI } from '../../api/incentive';

interface IncentivePreviewCardProps {
  onFileUpload?: (file: File) => Promise<void> | void;
  onDownloadTemplate?: () => Promise<void> | void;
  isUploading?: boolean;
}

export const IncentivePreviewCard: React.FC<IncentivePreviewCardProps> = ({
  onFileUpload,
  onDownloadTemplate,
  isUploading = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const processFile = async (file: File) => {
    setUploadedFileName(file.name);
    if (onFileUpload) {
      await onFileUpload(file);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await processFile(file);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      await processFile(file);
    }
  };

  const handleDownload = async () => {
    if (onDownloadTemplate) {
      await onDownloadTemplate();
      return;
    }
    try {
      setDownloading(true);
      await downloadIncentiveTemplateAPI();
    } catch (err) {
      console.warn('Backend template download failed, using fallback CSV:', err);
      const csvContent = 'data:text/csv;charset=utf-8,Zone,City,DistributorCode,TargetUnits,IncentiveRate\nMumbai,Vashi,DIST-9928,250,1500\n';
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'incentive_target_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadedFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm h-full flex flex-col justify-between">
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-0.5">
          Bulk Target Assignment
        </h3>
        <p className="text-xs text-gray-400 font-medium mb-5">
          Upload multiple target thresholds to assign customized volumes instantly.
        </p>

        {/* Upload Box */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all duration-200 ${
            dragOver
              ? 'border-red-500 bg-red-50/50 scale-[1.01]'
              : 'border-red-200 hover:border-red-400 bg-red-50/20'
          }`}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx, .xls, .csv"
            className="hidden"
          />

          <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-3">
            {isUploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#C1272D]" />
            ) : (
              <Upload className="w-6 h-6" />
            )}
          </div>

          {uploadedFileName ? (
            <div className="flex items-center justify-center gap-2 text-xs font-bold text-emerald-700">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="truncate max-w-[180px]">{uploadedFileName}</span>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-gray-400 hover:text-rose-600 p-0.5 rounded cursor-pointer transition-colors"
                title="Remove file"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <>
              <div className="text-xs font-bold text-gray-900 mb-1">
                Drag Excel or CSV file here to upload
              </div>
              <div className="text-[11px] text-gray-400 font-medium">
                Supported formats: Excel (.xlsx, .xls), CSV (Max 5MB)
              </div>
            </>
          )}
        </div>
      </div>

      {/* Download Template Button */}
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="mt-5 w-full bg-rose-50 hover:bg-rose-100 text-[#B71C1C] border border-rose-200 rounded-xl py-2.5 px-4 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50"
      >
        {downloading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        <span>Download Excel / CSV Layout Template</span>
      </button>
    </div>
  );
};
